import hash from 'object-hash';
import debounce from 'debounce';
import { trackEventSchema } from './schema';
import { getConfig } from './state';
import { isInsideAppContainer } from '../../../global-utils/vmFunctions/others';
import { isArr } from '../../../global-utils';
import { getIsAdminPreview } from '../../../shared/admin-preview';
import { emptyNull } from '../../../shared/misc';
import storageAndCookie from '../../../global-utils/storage-and-cookies';

/* tracks is a map of the debounced functions per `detail` object.
 keys are hashed detail objects (important that before hash they are sorted)
 values are debounced-dispatch track functions https://www.npmjs.com/package/debounce.
 Once there is a request to track event, we save debounced-dispatchTrack to Map
 After debounceInterval, dispatchTrack gets called and then the value
 for is clear from Map. */
const tracks = new Map();

const dispatchTrack = (h, detail) => () => {
  if (getIsAdminPreview()) return;
  if (!window.DataInsightTracking?.trackingListener) return;
  const ev = new CustomEvent('data-insight.track', { detail });
  window.dispatchEvent(ev);
  tracks.delete(h);
};

export const dataInsightTrack = (params) => {
  const config = getConfig();
  if (!config) return; // Module was not initialized

  // detail - set of properties passed to tracking event
  const { debounceInterval = config.debounceInterval, ...detail } = params;
  const { success, data, error } = trackEventSchema.safeParse(detail);

  if (!success) {
    console.log(error.format());
    throw new Error('data-insight: wrong parametrization', {
      cause: detail
    });
  }

  const h = hash(detail);
  if (!tracks.has(h)) {
    tracks.set(h, debounce(dispatchTrack(h, data), debounceInterval));
  }

  tracks.get(h)();
};

export const trackPage = (params) => dataInsightTrack({ sourceApplicationAction: 'vmx-page', ...params });
export const trackPageProgram = (params) => dataInsightTrack({ sourceApplicationAction: 'vmx-page-program', ...params });
export const trackPageProfile = (params) => dataInsightTrack({ sourceApplicationAction: 'vmx-page-profile', ...params });

export const dataInsightInit = (user = emptyNull) => {
  const { authKey: dataInsightAuthKey, trackImportId } = getConfig();
  const detail = {
    dataInsightAuthKey,
    user: {
      ...user,
      EventDevice: isInsideAppContainer() ? 'vmx-mobile-app' : null
    }
  };

  // PAG expects the ImportId in cookie:
  if (trackImportId) {
    storageAndCookie.set('sso_id', user.UserExternalId ?? null, null, true);
  }

  const ev = new CustomEvent('data-insight.init', { detail });
  window.dispatchEvent(ev);
};

const getFirstParticipantTypeName = (pArr) => {
  // Handle case where pArr is an object with numbered keys
  if (pArr && typeof pArr === 'object') {
    // Get all keys that are numbers (like "0", "1", etc.)
    const numericKeys = Object.keys(pArr)
      .filter((key) => !Number.isNaN(Number(key)))
      .sort((a, b) => Number(a) - Number(b)); // Sort numerically

    // Find first item with ParticipantTypeName
    const foundKey = numericKeys?.find((key) => pArr?.[key]?.ParticipantTypeName);
    return foundKey ? pArr?.[foundKey]?.ParticipantTypeName : null;
  }

  return null;
};

const resolveUserExternalId = (config) => (resp) => {
  // if ImportId is undefined coerce to null
  const { ImportId = null } = resp;

  if (config.trackImportId) return ImportId;

  // if switch is false, set to null
  return null;
};

// @pArr - array of responses of API calls
export const extendUser = (userLoginInfo = emptyNull) => (pArr) => {
  // @reduce - combine API resp objects into single obj
  const resp = pArr.reduce((acc, el) => ({ ...acc, ...el }), emptyNull);
  const firstParticipantTypeName = getFirstParticipantTypeName(resp);
  const config = getConfig();

  const { user = emptyNull } = userLoginInfo; // https://jmarquardt.atlassian.net/browse/MEV-3917
  const { partner_id: UserId } = user ?? emptyNull;
  const { city: UserCity, country: UserCountryCode } = user;

  return {
    ...resp,
    UserIsHcp: resp.HealthCareProfessional,
    UserAreaOfInterest: isArr(resp.SelectedInterests) ? resp.SelectedInterests.join('|') : null,
    UserGender: resp.GenderExtended,
    UserProfession: resp.Profession,
    UserVisitorType: resp.ParticipantTypeName ?? firstParticipantTypeName,
    UserId,
    UserCity,
    UserCountryCode,
    UserExternalId: resolveUserExternalId(config)(resp)
  };
};
